/**
 * EthereumWalletContext
 * Manages Ethereum wallet connection with multi-chain support.
 *
 * The hook (useEthereumWallet) is in useEthereumWallet.ts to satisfy
 * react-refresh/only-export-components — a file with a provider component
 * must not also export hooks.
 */

import { useModal } from 'connectkit';
import { useCallback, type ReactNode } from 'react';
import { useAccount, useDisconnect, useBalance, useSwitchChain } from 'wagmi';

import { useChainContext } from './useChain';
import { EthereumWalletContext } from './ethereumWalletContextDef';
import type { EthereumWalletContextType } from './ethereumWalletContextDef';

export type { EthereumWalletContextType } from './ethereumWalletContextDef';

interface EthereumWalletProviderProps {
  children: ReactNode;
}

export const EthereumWalletProvider = ({ children }: EthereumWalletProviderProps) => {
  const { address, isConnected, isConnecting, chainId } = useAccount();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitchingChain } = useSwitchChain();
  const { setOpen } = useModal();

  // Get selected chain from ChainContext
  const { selectedChainId } = useChainContext();

  // Get balance for the selected chain
  const { data: balanceData } = useBalance({
    address,
    chainId: selectedChainId ?? undefined,
  });

  const openConnectModal = () => setOpen(true);

  // Check if connected to wrong network (different from selected chain)
  const isWrongNetwork = isConnected && chainId !== selectedChainId;

  // Switch to the selected chain
  const switchToSelectedChain = useCallback(async () => {
    if (switchChain && selectedChainId) {
      switchChain({ chainId: selectedChainId });
    }
  }, [switchChain, selectedChainId]);

  // Legacy method for backward compatibility
  const switchToSepolia = useCallback(async () => {
    if (switchChain) {
      switchChain({ chainId: 11155111 }); // Sepolia chain ID
    }
  }, [switchChain]);

  const value: EthereumWalletContextType = {
    address,
    isConnected,
    isConnecting,
    chainId,
    isWrongNetwork,
    expectedChainId: selectedChainId,
    openConnectModal,
    disconnect,
    switchToSelectedChain,
    switchToSepolia,
    isSwitchingChain,
    balance: balanceData?.formatted,
    balanceSymbol: balanceData?.symbol,
  };

  return (
    <EthereumWalletContext.Provider value={value}>
      {children}
    </EthereumWalletContext.Provider>
  );
};
